import React from 'react';
import {
  X, Smartphone, Headphones, Monitor
} from 'lucide-react';

/* ─── Phone Frame wrapper (image-based) ─── */
const ScreenFrame = ({ label, src }) => (
  <div className="flex-shrink-0 flex flex-col items-center gap-3">
    <div className="w-[260px] md:w-[280px]">
      <div className="bg-slate-800 rounded-[28px] border border-slate-700 p-2 shadow-2xl shadow-black/40">
        <div className="bg-white rounded-[20px] overflow-hidden">
          <img src={src} alt={label} className="w-full h-auto" loading="lazy" />
        </div>
      </div>
    </div>
    <span className="text-[11px] text-slate-400 font-medium">{label}</span>
  </div>
);

/* ═══════════════════ AGENT DASHBOARD DETAIL ═══════════════════ */
const AgentDashboardDetail = () => (
  <div className="space-y-14">
    {/* Section: Homescreen & Profile */}
    <div>
      <h3 className="text-white font-bold text-lg mb-2">Homescreen & Sakhi Profile</h3>
      <p className="text-slate-400 text-sm mb-6 max-w-2xl">The agent's landing screen provides performance metrics, quick actions, upcoming deliveries, and access to beneficiary profiles with complete history.</p>
      <div className="flex gap-8 overflow-x-auto pb-4">
        <ScreenFrame label="Agent Homescreen" src="https://customer-assets.emergentagent.com/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/hls346qm_Homescreen%20v3.png" />
        <ScreenFrame label="Sakhi (Beneficiary) Profile" src="https://customer-assets.emergentagent.com/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/zapq793k_Sakhi%20Profile%20v2.png" />
      </div>
    </div>

    {/* Section: Add New Beneficiary Flow */}
    <div>
      <h3 className="text-white font-bold text-lg mb-2">Add New Beneficiary Flow</h3>
      <p className="text-slate-400 text-sm mb-6 max-w-2xl">A guided process with dual verification (phone OTP + Aadhar) to ensure every beneficiary in the database is genuine and verified.</p>
      <div className="flex gap-8 overflow-x-auto pb-4">
        <ScreenFrame label="Step 1: Check Existing User" src="https://customer-assets.emergentagent.com/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/m71rfmvk_Check%20if%20user%20exists%20in%20system.png" />
        <ScreenFrame label="Step 2: Validate Phone Number" src="https://customer-assets.emergentagent.com/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/2zz0e11l_NB%202%20Validate%20Phone%20Number.png" />
        <ScreenFrame label="Step 3: Add Personal Details" src="https://customer-assets.emergentagent.com/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/wmjl5dxr_Add%20Beneficiary%20Details.png" />
        <ScreenFrame label="Step 3: AADHAR Verification" src="https://customer-assets.emergentagent.com/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/oao6r8wz_Add%20AADHAR%20Photos%20.png" />
        <ScreenFrame label="Step 4: Verify OTP" src="https://customer-assets.emergentagent.com/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/ucb6oa00_Verify%20OTP%20from%20Beneficiary.png" />
        <ScreenFrame label="Step 5: Member Added" src="https://customer-assets.emergentagent.com/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/zrk6hloc_Member%20Added%20Success.png" />
      </div>
    </div>

    {/* Section: Fulfill Order Flow */}
    <div>
      <h3 className="text-white font-bold text-lg mb-2">Fulfill Order Flow</h3>
      <p className="text-slate-400 text-sm mb-6 max-w-2xl">OTP-verified handoff ensures every delivery reaches the intended beneficiary. Search by Aadhar, verify via OTP, and confirm handover — all trackable in real time.</p>
      <div className="flex gap-8 overflow-x-auto pb-4">
        <ScreenFrame label="Step 1: Search by Aadhar" src="https://customer-assets.emergentagent.com/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/jtil44sj_Search%20the%20Beneficiary.png" />
        <ScreenFrame label="Step 2: Browse Results" src="https://customer-assets.emergentagent.com/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/sdnc84ou_FO%20Browse%20through%20Search%20Results.png" />
        <ScreenFrame label="Step 3: Verify OTP & Handover" src="https://customer-assets.emergentagent.com/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/cwioo2cs_FO%20Verify%20OTP%20.png" />
        <ScreenFrame label="Step 4: Order Fulfilled" src="https://customer-assets.emergentagent.com/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/f32t99jf_FO%20Success.png" />
      </div>
    </div>
  </div>
);

/* ═══════════════════ SUPPORT DASHBOARD DETAIL ═══════════════════ */
const SupportDashboardDetail = () => (
  <div className="space-y-12">
    <div>
      <h3 className="text-white font-bold text-lg mb-2">Support Dashboard — Overview</h3>
      <p className="text-slate-400 text-sm mb-6 max-w-2xl">Tablet and desktop interface for supervisors managing agent clusters. Includes villager data, active agent tracking, recent activities, agent information, and quick actions.</p>
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-3xl blur-2xl" />
        <div className="relative bg-slate-900/80 border border-slate-800 rounded-2xl p-3 md:p-5">
          <img
            src="https://customer-assets.emergentagent.com/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/49me7vnc_Support%20Dashboard%20Overview.png"
            alt="Support Dashboard — Villager Data, Active Agents, Agent Information Table"
            className="w-full rounded-xl"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  </div>
);

/* ═══════════════════ CLIENT/PATRON DASHBOARD DETAIL ═══════════════════ */
const ClientDashboardDetail = () => (
  <div className="space-y-12">
    <div>
      <h3 className="text-white font-bold text-lg mb-2">Leader Dashboard — Overview</h3>
      <p className="text-slate-400 text-sm mb-6 max-w-2xl">High-level analytics for constituency leaders. Taluka-wise registration, age demographics, beneficiary stats, distribution metrics, report exports, and system status — all in one view.</p>
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-3xl blur-2xl" />
        <div className="relative bg-slate-900/80 border border-slate-800 rounded-2xl p-3 md:p-5">
          <img
            src="https://customer-assets.emergentagent.com/job_f3f3dd43-5861-424c-bd43-fa6ef34a3548/artifacts/ao8hhge4_Leader%20Dashboard%20Overview.png"
            alt="Leader Dashboard — Beneficiary Stats, Taluka-wise Registration, Age Demographics, Reports"
            className="w-full rounded-xl"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  </div>
);

/* ═══════════════════ OVERLAY MODAL ═══════════════════ */
export const DashboardDetailOverlay = ({ dashboard, onClose }) => {
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const config = {
    agent: { title: 'Agent Dashboard', subtitle: 'Mobile App for Field Karyakartas', color: 'from-cyan-500 to-blue-500', icon: Smartphone, Component: AgentDashboardDetail },
    support: { title: 'Support Dashboard', subtitle: 'Tablet Interface for Supervisors', color: 'from-amber-500 to-orange-500', icon: Headphones, Component: SupportDashboardDetail },
    client: { title: 'Leader Dashboard', subtitle: 'Analytics Portal for Leaders', color: 'from-emerald-500 to-teal-500', icon: Monitor, Component: ClientDashboardDetail }
  }[dashboard];

  if (!config) return null;
  const { title, subtitle, color, icon: Icon, Component } = config;

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950/95 backdrop-blur-sm overflow-y-auto" data-testid={`dashboard-detail-${dashboard}`}>
      {/* sticky header */}
      <div className="sticky top-0 z-10 bg-slate-950/90 backdrop-blur border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">{title}</h2>
              <p className="text-slate-400 text-xs">{subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors"
            data-testid="dashboard-detail-close"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
      </div>
      {/* content */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        <Component />
      </div>
    </div>
  );
};
